/**
 * 图片任务节点 - 展示图片生成任务的状态和结果
 * 自动轮询更新任务状态，展示生成的图片
 */
import React, { memo, useState, useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { NodeLayout } from './NodeLayout';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { useProject } from '../providers/ProjectProvider';
import { toast } from 'react-toastify';
import './ImageTaskNode.css';

const apiService = new ApiService();

const TRANSFERRED_IMAGE_URL_PREFIXES = [
  'https://st-video.cc',
  'https://cdn-video.51sux.com',
  'https://cdn-ali-hk.51sux.com',
  'https://cdn-hk.51sux.com',
];

const getImageUrl = (image) => (typeof image === 'string' ? image : image?.url);

const areImagesTransferred = (images) => (
  Array.isArray(images)
  && images.length > 0
  && images.every(image => {
    const url = getImageUrl(image);
    return Boolean(url) && TRANSFERRED_IMAGE_URL_PREFIXES.some(prefix => url.startsWith(prefix));
  })
);

export const ImageTaskNode = memo(({ id, data }) => {
  const { updateNodeData } = useReactFlow();
  const { project, readOnly } = useProject(); // 获取项目信息和只读状态
  const [status, setStatus] = useState(data.status || 'pending');
  const [generatedImages, setGeneratedImages] = useState(data.generatedImages || []);
  const [error, setError] = useState(data.error || null);
  const [inputParams, setInputParams] = useState(data.inputParams || {});
  const [outputParams, setOutputParams] = useState(data.outputParams || null);
  const [isTransferring, setIsTransferring] = useState(false); // 图片转存状态
  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const hasCheckedTransferRef = useRef(false); // 标记是否已检查过转存状态
  const previousTransferStatusRef = useRef(null); // 记录上一次的转存状态，用于检测状态变化
  const thumbnailUpdateTriggeredRef = useRef(false); // 每个任务只触发一次封面更新

  const taskId = data.taskId;
  const taskType = data.taskType || 'text-to-image'; // 'text-to-image' 或 'reference-to-image'
  const apiTaskType = data.apiTaskType || null; // 'coze_v2_api'、'rep_api' 或 'v3_api'

  // 更新项目封面（新完成的任务只触发一次）
  const updateProjectThumbnail = async (imageUrl) => {
    if (!imageUrl) {
      return;
    }

    if (readOnly) {
      return;
    }
    
    // 检查是否有项目信息
    if (!project || !project.uuid) {
      console.log('ImageTaskNode - 无项目信息，跳过封面更新');
      return;
    }
    
    try {
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        console.log('ImageTaskNode - 未登录，跳过封面更新');
        return;
      }
      
      console.log(`ImageTaskNode ${id} - 开始更新项目封面:`, imageUrl);
      
      await apiService.updateDrawProject(
        userData.token, 
        project.uuid, 
        { thumbnail_url: imageUrl }
      );
      
      console.log(`ImageTaskNode ${id} - 项目封面更新成功`);
      toast.success('项目封面已自动更新');
    } catch (err) {
      console.error('ImageTaskNode - 更新项目封面失败:', err);
      // 静默失败，不影响用户使用
    }
  };

  // 轮询查询任务状态
  const pollTaskStatus = async () => {
    if (!taskId || !mountedRef.current) return;

    // React 17 会在下面 setStatus 后立即重跑 effect。保留本轮查询开始时的
    // 状态，避免完成态 effect 在图片解析结束前覆盖转存状态标记。
    const wasTaskInProgress = status === 'pending' || status === 'processing';

    try {
      // 根据任务类型选择不同的 API 端点
      const isCozeV2Task = apiTaskType === 'coze_v2_api';
      const isV3ApiTask = apiTaskType === 'v3_api';
      let result;
      if (isV3ApiTask) {
        const v3Result = await apiService.queryV3Task(taskId);
        result = {
          ...v3Result,
          data: v3Result?.data ? {
            ...v3Result.data,
            input_params: v3Result.data.params || {},
            output_params: v3Result.data.output || v3Result.data.result || null,
          } : null,
        };
      } else {
        const apiEndpoint = isCozeV2Task
          ? `${apiService.apiBase}/v2/tasks/info`
          : `${apiService.apiBase}/fal/tasks/info`;
        result = await apiService.request(
          apiEndpoint,
          'POST',
          { task_id: taskId }
        );
      }

      if (!mountedRef.current) return;

      if (result.code === 200 && result.data) {
        const newStatus = result.data.status;
        setStatus(newStatus);
        setInputParams(result.data.input_params || {});
        setOutputParams(result.data.output_params || null);

        if (newStatus === 'completed') {
          // 任务完成，提取图片URL
          let outputData = [];
          
          // 添加调试日志
          console.log(`ImageTaskNode ${id} - 任务类型:`, taskType);
          console.log(`ImageTaskNode ${id} - 输出参数:`, result.data.output_params);
          
          // coze_v2_api 返回格式：output_params 是 JSON 字符串，包含 images 数组
          if (isCozeV2Task || taskType === 'banana-pro-cost-effective') {
            let parsedOutput = result.data.output_params;
            // 如果 output_params 是字符串，需要先解析
            if (typeof parsedOutput === 'string') {
              try {
                parsedOutput = JSON.parse(parsedOutput);
                console.log(`ImageTaskNode ${id} - 解析 coze_v2_api output_params:`, parsedOutput);
              } catch (e) {
                console.warn(`ImageTaskNode ${id} - 解析 output_params 失败:`, e);
                parsedOutput = {};
              }
            }
            
            // 检查 raw_output 字段（新结构支持）
            if (parsedOutput.raw_output) {
              let rawOutput = parsedOutput.raw_output;
              if (typeof rawOutput === 'string') {
                try {
                  rawOutput = JSON.parse(rawOutput);
                  console.log(`ImageTaskNode ${id} - 解析 raw_output 字符串:`, rawOutput);
                } catch (e) {
                  // 如果解析失败，可能本身就是URL字符串
                  if (rawOutput.startsWith('http')) {
                    console.log(`ImageTaskNode ${id} - raw_output 直接是URL:`, rawOutput);
                    outputData = [rawOutput];
                  }
                }
              }
              // 如果 raw_output 是对象，使用它作为新的 parsedOutput 继续解析
              if (typeof rawOutput === 'object' && rawOutput !== null) {
                console.log(`ImageTaskNode ${id} - 使用 raw_output 对象作为数据源:`, rawOutput);
                parsedOutput = rawOutput;
              }
            }
            
            // 如果还没有找到图片，继续解析
            if (outputData.length === 0) {
              // 从 images 数组中提取 URL
              if (parsedOutput.images && Array.isArray(parsedOutput.images) && parsedOutput.images.length > 0) {
                outputData = parsedOutput.images.map(img => img.url || img);
                console.log(`ImageTaskNode ${id} - coze_v2_api images:`, outputData);
              }
              // 即梦4.1：data 是数组，每项是 { url: "..." }
              else if (parsedOutput.data && Array.isArray(parsedOutput.data)) {
                outputData = parsedOutput.data.map(item => item.url || item);
                console.log(`ImageTaskNode ${id} - coze_v2_api data 数组 (即梦4.1):`, outputData);
              }
              // 香蕉性价比版：data 是字符串
              else if (parsedOutput.data && typeof parsedOutput.data === 'string') {
                outputData = [parsedOutput.data];
                console.log(`ImageTaskNode ${id} - coze_v2_api data 字符串 (香蕉性价比版):`, outputData);
              } else {
                outputData = [];
                console.log(`ImageTaskNode ${id} - coze_v2_api 未找到 images/data`);
              }
            }
          }
          // Google Banana 和 Banana Pro 标准版的输出格式
          else if (taskType === 'google-banana' || taskType === 'banana-pro') {
            console.log(`ImageTaskNode ${id} - ${taskType} output_params:`, result.data.output_params);
            
            // 优先检查 images 数组（新格式）
            if (result.data.output_params?.images && Array.isArray(result.data.output_params.images)) {
              outputData = result.data.output_params.images.map(img => img.url || img);
              console.log(`ImageTaskNode ${id} - 使用 images 数组:`, outputData);
            }
            // 其次检查 output 字符串（旧格式）
            else if (typeof result.data.output_params?.output === 'string') {
              outputData = [result.data.output_params.output];
              console.log(`ImageTaskNode ${id} - 使用 output 字符串:`, outputData);
            }
            // 最后检查 image.url
            else if (result.data.output_params?.image?.url) {
              outputData = [result.data.output_params.image.url];
              console.log(`ImageTaskNode ${id} - 使用 image.url:`, outputData);
            } else {
              outputData = [];
              console.log(`ImageTaskNode ${id} - 未找到图片 URL`);
            }
          } 
          // 其他任务的输出格式（支持 output 数组或 images 数组）
          else {
            // 优先使用 output 字段
            if (result.data.output_params?.output) {
              outputData = result.data.output_params.output;
              console.log(`ImageTaskNode ${id} - 使用 output 字段:`, outputData);
            }
            // 其次使用 images 字段（即梦4.0等返回格式）
            else if (result.data.output_params?.images && Array.isArray(result.data.output_params.images)) {
              outputData = result.data.output_params.images.map(img => img.url || img);
              console.log(`ImageTaskNode ${id} - 使用 images 字段:`, outputData);
            }
            else {
              outputData = [];
              console.log(`ImageTaskNode ${id} - 未找到 output 或 images 字段`);
            }
          }
          
          console.log(`ImageTaskNode ${id} - 最终 outputData:`, outputData);
          
          // 处理返回数据：output可能是字符串数组或对象数组
          const normalizedOutputData = Array.isArray(outputData)
            ? outputData
            : (outputData ? [outputData] : []);
          const images = normalizedOutputData.map((item, index) => {
            if (typeof item === 'string') {
              return {
                url: item,
                width: null,
                height: null,
                index: index
              };
            } else {
              return item;
            }
          });
          
          setGeneratedImages(images);
          setError(null);
          
          // 检查图片是否已经转存到自己的服务器
          const allImagesTransferred = areImagesTransferred(images);
          
          // 标记已完成转存检查
          hasCheckedTransferRef.current = true;
          
          if (allImagesTransferred) {
            // 所有图片都已转存，停止轮询
            console.log(`ImageTaskNode ${id} - 所有图片已转存到自有 CDN，停止轮询`);
            setIsTransferring(false);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // 新 V3 链路在转存期间不返回图片，首次完成时可能直接得到 CDN URL；
            // 因此除了 false -> true，也要识别“本轮查询开始时任务仍在进行中”。
            // 一次性标记可防止并发轮询重复提交，同时不覆盖刷新后加载的历史任务。
            const shouldUpdateThumbnail = (
              !thumbnailUpdateTriggeredRef.current
              && (previousTransferStatusRef.current === false || wasTaskInProgress)
            );
            if (shouldUpdateThumbnail) {
              thumbnailUpdateTriggeredRef.current = true;
              console.log(`ImageTaskNode ${id} - 检测到转存状态变化（未转存->已转存），触发封面更新`);
              updateProjectThumbnail(images[0]?.url);
            }
            
            // 更新转存状态记录
            previousTransferStatusRef.current = true;
          } else {
            // 图片还未转存，继续轮询但降低频率到30秒
            console.log(`ImageTaskNode ${id} - 图片还未转存，继续30秒轮询`);
            setIsTransferring(true);
            
            // 更新转存状态记录
            previousTransferStatusRef.current = false;
            
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            // 设置30秒轮询
            pollingIntervalRef.current = setInterval(() => {
              pollTaskStatus();
            }, 30000); // 30秒
          }
        } else if (newStatus === 'failed') {
          const outputError = result.data.output_params?.error;
          const failureMessage = result.data.error
            || (typeof outputError === 'string' ? outputError : outputError?.message)
            || result.data.output_params?.message
            || '生成失败';
          setError(failureMessage);
          
          // 停止轮询
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }
    } catch (err) {
      console.error('查询任务状态失败:', err);
    }
  };

  // 同步状态到节点数据（关键：让其他节点能读取到更新）
  useEffect(() => {
    updateNodeData(id, {
      status,
      generatedImages,
      error,
      inputParams,
      outputParams,
    });
    console.log(`ImageTaskNode ${id} - 更新节点数据:`, { status, generatedImages: generatedImages.length });
  }, [id, status, generatedImages, error, inputParams, outputParams, updateNodeData]);

  // 检查图片是否需要转存
  const checkImageTransfer = (images) => {
    if (!images || images.length === 0) return false;

    return !areImagesTransferred(images); // 返回是否需要转存
  };

  // 启动轮询
  useEffect(() => {
    mountedRef.current = true;

    if (!taskId) return;

    // 如果任务还在进行中，启动快速轮询（3秒）
    if (status === 'pending' || status === 'processing') {
      // 如果已经有轮询，不重复创建
      if (!pollingIntervalRef.current) {
        console.log(`ImageTaskNode ${id} - 启动任务状态轮询（3秒）`);
        hasCheckedTransferRef.current = false; // 重置转存检查标记

        // V3 在转存期间不会返回图片，首次拿到结果时可能已经是 CDN URL。
        // 从进行中状态开始轮询，等价于已经观察到“尚未转存完成”。
        previousTransferStatusRef.current = false;
        
        // 立即查询一次
        pollTaskStatus();
        
        // 启动定时轮询（每3秒）
        pollingIntervalRef.current = setInterval(() => {
          pollTaskStatus();
        }, 3000);
      }
    }
    // 如果任务已完成，检查是否需要图片转存轮询
    else if (status === 'completed') {
      // 状态和图片数据由不同的 setState 更新；等待图片数据就绪，避免把
      // “刚完成但尚未解析结果”误判为“历史任务已经转存完成”。
      // 注意：pollTaskStatus 内部会处理转存检查，这里只处理页面刷新的情况
      if (
        generatedImages
        && generatedImages.length > 0
        && !hasCheckedTransferRef.current
        && !pollingIntervalRef.current
      ) {
        hasCheckedTransferRef.current = true; // 标记已检查
        
        if (checkImageTransfer(generatedImages)) {
          console.log(`ImageTaskNode ${id} - 页面加载时检测到图片未转存，启动30秒轮询`);
          setIsTransferring(true);
          
          // 初始化转存状态为false（未转存）
          previousTransferStatusRef.current = false;
          
          // 立即查询一次
          pollTaskStatus();
          
          // 启动慢速轮询（每30秒）
          pollingIntervalRef.current = setInterval(() => {
            pollTaskStatus();
          }, 30000);
        } else {
          console.log(`ImageTaskNode ${id} - 页面加载时检测到所有图片已转存，无需轮询和更新`);
          setIsTransferring(false);
          
          // 🎯 重要：页面刷新时，图片已转存，设置状态为true，不触发更新
          previousTransferStatusRef.current = true;
        }
      }
    }

    // 清理函数
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [taskId, status]);

  // 手动刷新
  const handleRefresh = () => {
    if (status !== 'completed' && status !== 'failed') {
      pollTaskStatus();
      toast.info('正在刷新任务状态...');
    }
  };

  // 下载图片（使用 fetch + blob 方式实现真正下载，避免跳转）
  const handleDownload = async (imageUrl, index) => {
    if (!imageUrl) return;

    try {
      toast.info('正在下载图片...');
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // 从 URL 中提取文件名，或使用默认名称
      const fileName = imageUrl.split('/').pop() || `generated-image-${index + 1}.png`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('图片下载成功');
    } catch (error) {
      console.error('下载图片失败:', error);
      toast.error('下载失败，请右键另存为');
      // 降级：打开新窗口
      window.open(imageUrl, '_blank');
    }
  };

  // 获取状态标签
  const getStatusBadge = () => {
    const statusConfig = {
      pending: { text: '排队中', className: 'ImageTaskNode-status-pending' },
      processing: { text: '生成中', className: 'ImageTaskNode-status-processing' },
      completed: { text: '已完成', className: 'ImageTaskNode-status-completed' },
      failed: { text: '失败', className: 'ImageTaskNode-status-failed' },
    };

    const config = statusConfig[status] || { text: status, className: '' };
    
    return (
      <span className={`ImageTaskNode-status-badge ${config.className}`}>
        {config.text}
      </span>
    );
  };

  // 获取任务类型标签
  const getTaskTypeLabel = () => {
    if (taskType === 'gpt-image-2') return 'GPT 2 图像生成';
    if (taskType === 'nano-banana-2') return 'Nano 2 图像生成';
    if (taskType === 'reference-to-image') return '图生图';
    if (taskType === 'google-banana') return '🍌 Banana';
    if (taskType?.startsWith('minimax-')) return '海螺图片';
    return '文生图';
  };

  return (
    <NodeLayout 
      id={id} 
      type="imageTask" 
      title={`${getTaskTypeLabel()}任务`}
      showInputHandle={true}
      showOutputHandle={true}
    >
      <div className="ImageTaskNode-wrapper">
        {/* 任务信息头部 */}
        <div className="ImageTaskNode-header">
          <div className="ImageTaskNode-task-id">
            任务ID: {taskId ? taskId.slice(0, 8) + '...' : 'N/A'}
          </div>
          {getStatusBadge()}
        </div>
        {Number.isFinite(Number(data.chargedPoints)) && (
          <div className="ImageTaskNode-charge">
            已预扣 {Number(data.chargedPoints).toLocaleString('zh-CN')} 积分
          </div>
        )}

        {/* 图片转存提示（静默） */}
        {isTransferring && status === 'completed' && (
          <div className="ImageTaskNode-transfer-hint">
            🔄 图片转存中...
          </div>
        )}

        {/* 输入参数 */}
        {inputParams && Object.keys(inputParams).length > 0 && (
          <div className="ImageTaskNode-params">
            <div className="ImageTaskNode-params-title">参数</div>
            <div className="ImageTaskNode-params-grid">
              {inputParams.size && (
                <div className="ImageTaskNode-param-item">
                  <span className="ImageTaskNode-param-label">分辨率:</span>
                  <span className="ImageTaskNode-param-value">{inputParams.size}</span>
                </div>
              )}
              {inputParams.aspect_ratio && (
                <div className="ImageTaskNode-param-item">
                  <span className="ImageTaskNode-param-label">宽高比:</span>
                  <span className="ImageTaskNode-param-value">{inputParams.aspect_ratio}</span>
                </div>
              )}
              {inputParams.image_input && (
                <div className="ImageTaskNode-param-item">
                  <span className="ImageTaskNode-param-label">参考图:</span>
                  <span className="ImageTaskNode-param-value">
                    {Array.isArray(inputParams.image_input) 
                      ? `${inputParams.image_input.length} 张` 
                      : '1 张'}
                  </span>
                </div>
              )}
            </div>
            {inputParams.prompt && (
              <div className="ImageTaskNode-prompt">
                <div className="ImageTaskNode-prompt-label">提示词:</div>
                <div className="ImageTaskNode-prompt-text">{inputParams.prompt}</div>
              </div>
            )}
          </div>
        )}

        {/* 进行中状态 */}
        {(status === 'pending' || status === 'processing') && (
          <div className="ImageTaskNode-loading">
            <div className="ImageTaskNode-spinner" />
            <div className="ImageTaskNode-loading-text">
              {status === 'pending' ? '任务排队中，请稍候...' : '正在生成图片...'}
            </div>
            <button 
              onClick={handleRefresh}
              disabled={readOnly}
              className="ImageTaskNode-refresh-button"
            >
              🔄 刷新状态
            </button>
          </div>
        )}

        {/* 生成成功 */}
        {status === 'completed' && generatedImages.length > 0 && (
          <div className="ImageTaskNode-results">
            <div className="ImageTaskNode-results-title">
              生成结果 ({generatedImages.length} 张)
            </div>
            <div className="ImageTaskNode-images">
              {generatedImages.map((img, index) => (
                <div key={index} className="ImageTaskNode-image-item">
                  <img 
                    src={img.url} 
                    alt={`生成图片 ${index + 1}`}
                    className="ImageTaskNode-image"
                  />
                  {(img.width && img.height) && (
                    <div className="ImageTaskNode-image-info">
                      {img.width} × {img.height}
                    </div>
                  )}
                  <button
                    onClick={() => handleDownload(img.url, index)}
                    className="ImageTaskNode-download-link"
                    title="下载图片"
                  >
                    📥 下载
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 失败状态 */}
        {status === 'failed' && (
          <div className="ImageTaskNode-error">
            <div className="ImageTaskNode-error-icon">❌</div>
            <div className="ImageTaskNode-error-text">
              {error || '生成失败，请检查参数后重试'}
            </div>
            <button 
              onClick={handleRefresh}
              disabled={readOnly}
              className="ImageTaskNode-retry-button"
            >
              🔄 重新查询
            </button>
          </div>
        )}

        {/* 输出信息 */}
        {outputParams && outputParams.metrics && (
          <div className="ImageTaskNode-metrics">
            <div className="ImageTaskNode-metrics-title">耗时信息</div>
            <div className="ImageTaskNode-metrics-grid">
              {outputParams.metrics.predict_time && (
                <div className="ImageTaskNode-metric-item">
                  <span className="ImageTaskNode-metric-label">生成时间:</span>
                  <span className="ImageTaskNode-metric-value">
                    {outputParams.metrics.predict_time.toFixed(2)}秒
                  </span>
                </div>
              )}
              {outputParams.metrics.total_time && (
                <div className="ImageTaskNode-metric-item">
                  <span className="ImageTaskNode-metric-label">总耗时:</span>
                  <span className="ImageTaskNode-metric-value">
                    {outputParams.metrics.total_time.toFixed(2)}秒
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </NodeLayout>
  );
});

ImageTaskNode.displayName = 'ImageTaskNode';
